import { ApiService, getUser } from "./apiService"

export interface SmartSearchDebug {
    tookMs: number;
    // How many products the search could choose from - what is on sale, or on
    // sale in the one store.
    scope: number;
    // The listings, exactly as the app shows them.
    products: any[];
    pieces: { kind: string; phrase: string }[];
    categories: string[];
    gender: string;
    age: string;
    suggested: boolean;
    brandProducts: number | null;
    afterProblemsGoals: number;
    parts: string[];
    unmatched: string[];
}

// One line the search sends as it runs: a finished step, or the answer.
export type SmartSearchEvent =
    | { type: 'step'; step: string; pieces?: string[]; categories?: string[]; found?: number }
    | ({ type: 'done' } & SmartSearchDebug)
    | { type: 'error' };

// The same search, told step by step as the server runs it: each line of the
// answer is handed over as it arrives.
export const smartSearchStream = async (dto: { input: string; storeId?: string }, onEvent: (event: SmartSearchEvent) => void) => {
    const user = await getUser();
    const response = await fetch(`${process.env.REACT_APP_BASE_URL}/search/smart/stream`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...(user?.apiToken ? { Authorization: `Bearer ${user.apiToken}` } : {}) },
        body: JSON.stringify(dto),
    });
    if (!response.ok || !response.body) throw new Error(`Search failed with status ${response.status}`);

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    for (;;) {
        const { done, value } = await reader.read();
        buffer += decoder.decode(value ?? new Uint8Array(), { stream: !done });
        let end = buffer.indexOf('\n');
        while (end !== -1) {
            const line = buffer.slice(0, end).trim();
            buffer = buffer.slice(end + 1);
            if (line) onEvent(JSON.parse(line));
            end = buffer.indexOf('\n');
        }
        if (done) break;
    }
}

// The agent search: one model working the product graph with tools, with
// every tool call it made.
export interface AgentSearchDebug {
    tookMs: number;
    scope: number;
    model: string;
    products: any[];
    steps: { tool: string; args: any; result: string }[];
    usage: { inputTokens: number; cachedTokens: number; outputTokens: number } | null;
    costUsd: number;
}

export const agentSearch = async (dto: { input: string; storeId?: string }): Promise<AgentSearchDebug> => {
    return await ApiService.post(`search/agent`, dto);
}

// What the agent is doing, as it starts doing it: its own line to the shopper,
// in their language.
export interface AgentSearchProgress {
    step: 'tags' | 'products' | 'text' | 'details' | 'finishing';
    message?: string;
}

export type AgentSearchEvent =
    | ({ type: 'step' } & AgentSearchProgress)
    | ({ type: 'done' } & AgentSearchDebug)
    | { type: 'error' };

// The agent search, told call by call as the server runs it.
export const agentSearchStream = async (dto: { input: string; storeId?: string }, onEvent: (event: AgentSearchEvent) => void) => {
    const user = await getUser();
    const response = await fetch(`${process.env.REACT_APP_BASE_URL}/search/agent/stream`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...(user?.apiToken ? { Authorization: `Bearer ${user.apiToken}` } : {}) },
        body: JSON.stringify(dto),
    });
    if (!response.ok || !response.body) throw new Error(`Search failed with status ${response.status}`);

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    for (;;) {
        const { done, value } = await reader.read();
        buffer += decoder.decode(value ?? new Uint8Array(), { stream: !done });
        let end = buffer.indexOf('\n');
        while (end !== -1) {
            const line = buffer.slice(0, end).trim();
            buffer = buffer.slice(end + 1);
            if (line) onEvent(JSON.parse(line));
            end = buffer.indexOf('\n');
        }
        if (done) break;
    }
}

// The app's search, run as the app runs it, with what each step decided.
export const smartSearch = async (dto: { input: string; storeId?: string }): Promise<SmartSearchDebug> => {
    return await ApiService.post(`search/smart`, dto);
}
